import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Gift, Clock, Zap, Star } from 'lucide-react';
import popupCampaignService from '../services/popupCampaignService';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const PopupContainer = styled.div`
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(15, 23, 42, 0.8) 50%, rgba(30, 41, 59, 0.7) 100%),
              ${props => props.$backgroundImage ? `url('${props.$backgroundImage}')` : 'url(\'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80\')'} center/cover;
  border-radius: 1.5rem;
  padding: 2rem 1.5rem;
  max-width: 450px;
  width: 90%;
  position: relative;
  border: 2px solid rgba(56, 189, 248, 0.6);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.9), 0 0 30px rgba(56, 189, 248, 0.2);
  animation: slideUp 0.4s ease;
  backdrop-filter: blur(5px);

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(50px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 640px) {
    padding: 1.5rem 1rem;
    margin: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const PopupHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;

  .icon-wrapper {
    width: 3.5rem;
    height: 3.5rem;
    background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    animation: pulse 2s infinite;
    box-shadow: 0 10px 30px rgba(56, 189, 248, 0.6), 0 0 20px rgba(56, 189, 248, 0.3);

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
  }

  h2 {
    font-size: 1.8rem;
    font-weight: 900;
    margin-bottom: 0.3rem;
    background: linear-gradient(135deg, #ffffff 0%, #38bdf8 50%, #0ea5e9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
    letter-spacing: 2px;
    text-shadow: 0 2px 10px rgba(56, 189, 248, 0.5);
  }

  .subtitle {
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  }
`;

const PopupContent = styled.div`
  margin-bottom: 1.5rem;

  .offer-details {
    background: rgba(56, 189, 248, 0.2);
    border: 2px solid rgba(56, 189, 248, 0.6);
    border-radius: 1rem;
    padding: 1.2rem;
    margin-bottom: 1rem;
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 32px rgba(56, 189, 248, 0.2);

    .offer-title {
      font-size: 1.3rem;
      font-weight: 800;
      color: #ffffff;
      margin-bottom: 0.8rem;
      text-align: center;
      line-height: 1.3;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
    }

    .offer-features {
      list-style: none;

      li {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
        color: #ffffff;
        font-size: 0.9rem;
        font-weight: 600;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);

        .icon {
          color: #10b981;
          flex-shrink: 0;
          filter: drop-shadow(0 2px 4px rgba(16, 185, 129, 0.4));
        }
      }
    }
  }

  .timer {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.2) 100%);
    border: 2px solid rgba(239, 68, 68, 0.6);
    color: #ffffff;
    padding: 0.8rem;
    border-radius: 0.75rem;
    text-align: center;
    font-weight: 800;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    backdrop-filter: blur(10px);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
    box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
  }
`;

const PopupActions = styled.div`
  display: flex;
  gap: 0.8rem;

  .btn-primary {
    flex: 2;
    background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
    color: white;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 0.75rem;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 15px rgba(56, 189, 248, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(56, 189, 248, 0.4);
    }
  }

  .btn-secondary {
    flex: 1;
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.4);
    padding: 0.8rem 1rem;
    border-radius: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.85rem;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border-color: rgba(255, 255, 255, 0.6);
    }
  }

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.6rem;
    
    .btn-primary {
      font-size: 0.9rem;
      padding: 0.7rem 1.2rem;
    }
    
    .btn-secondary {
      font-size: 0.8rem;
      padding: 0.7rem;
    }
  }
`;

const PromotionPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCampaign = async () => {
      try {
        console.log('Chargement de la campagne popup...');
        setLoading(true);
        
        // Récupérer l'ID utilisateur si disponible
        const userId = localStorage.getItem('userId') || null;
        console.log('User ID:', userId);
        
        // Récupérer la campagne active depuis l'API
        const response = await popupCampaignService.getActiveCampaign(userId);
        console.log('Réponse API:', response);
        
        if (response.campaign) {
          const formattedCampaign = popupCampaignService.formatCampaignForPopup(response.campaign);
          console.log('Campagne formatée:', formattedCampaign);
          
          // Vérifier si le popup doit être affiché
          const lastPopupTime = popupCampaignService.getLastPopupTime();
          console.log('Dernier popup:', lastPopupTime);
          
          if (popupCampaignService.shouldShowPopup(formattedCampaign, lastPopupTime)) {
            console.log('Popup doit être affiché');
            // Afficher après le délai configuré
            setTimeout(() => {
              console.log('Affichage du popup...');
              setCampaign(formattedCampaign);
              setIsVisible(true);
              popupCampaignService.saveLastPopupTime();
            }, formattedCampaign.displayDelay || 1000);
          } else {
            console.log('Popup ne doit pas être affiché (fréquence)');
          }
        } else {
          console.log('Aucune campagne active');
        }
      } catch (error) {
        console.error('Error loading popup campaign:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCampaign();
  }, []);

  const handleClose = async () => {
    // Suivre l'interaction de fermeture
    if (campaign?.id) {
      try {
        await popupCampaignService.trackInteraction(campaign.id, 'close');
      } catch (error) {
        console.error('Error tracking close interaction:', error);
      }
    }
    
    setIsVisible(false);
  };

  const handleAction = async () => {
    // Suivre l'interaction de clic
    if (campaign?.id) {
      try {
        await popupCampaignService.trackInteraction(campaign.id, 'click');
      } catch (error) {
        console.error('Error tracking click interaction:', error);
      }
    }

    // Rediriger vers l'URL de la campagne
    if (campaign?.buttons?.primary?.url) {
      window.location.href = campaign.buttons.primary.url;
    }
    handleClose();
  };

  // Auto-fermeture après la durée configurée
  useEffect(() => {
    if (isVisible && campaign?.displayDuration) {
      const timer = setTimeout(() => {
        handleClose();
      }, campaign.displayDuration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, campaign]);

  if (loading) {
  console.log('Popup en chargement...');
  return null;
}

if (!isVisible || !campaign) {
  console.log('Popup non visible ou pas de campagne:', { isVisible, campaign });
  return null;
}

  const IconComponent = campaign.icon || Gift;

  return (
    <PopupOverlay onClick={handleClose}>
      <PopupContainer 
        onClick={(e) => e.stopPropagation()}
        $backgroundImage={campaign.backgroundImage}
      >
        <CloseButton onClick={handleClose}>
          <X size={20} />
        </CloseButton>

        <PopupHeader>
          <div className="icon-wrapper">
            <IconComponent size={24} color="white" />
          </div>
          <h2>{campaign.title}</h2>
          <p className="subtitle">{campaign.subtitle}</p>
        </PopupHeader>

        <PopupContent>
          <div className="offer-details">
            <h3 className="offer-title">{campaign.description}</h3>
            <ul className="offer-features">
              {campaign.features.map((feature, index) => (
                <li key={index}>
                  <Gift className="icon" size={16} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {campaign.timer?.show && (
            <div className="timer">
              <Clock size={16} />
              <span>{campaign.timer.display}</span>
            </div>
          )}
        </PopupContent>

        <PopupActions>
          <button className="btn-primary" onClick={handleAction}>
            {campaign.buttons?.primary?.text || 'En profiter maintenant'}
          </button>
          <button className="btn-secondary" onClick={handleClose}>
            {campaign.buttons?.secondary?.text || 'Plus tard'}
          </button>
        </PopupActions>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default PromotionPopup;
